import React, { useEffect, useRef } from "react";

const AIBackground = () => {
    const canvasRef = useRef(null);
    const mouse = useRef({
        x: null,
        y: null,
        active: false
    });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        let particles = [];
        let animationFrame;

        const resizeCanvas = () => {
            const section = canvas.parentElement;

            canvas.width = section.offsetWidth;
            canvas.height = section.offsetHeight;

            createParticles();
        };

        const createParticles = () => {
            particles = [];

            const count = Math.min(
                120,
                Math.floor((canvas.width * canvas.height) / 9000)
            );

            for (let i = 0; i < count; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,

                    vx: (Math.random() - 0.5) * 0.5,
                    vy: (Math.random() - 0.5) * 0.5,

                    size: Math.random() * 1.8 + 0.6,

                    opacity: Math.random() * 0.6 + 0.2
                });
            }
        };

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();

            mouse.current.x = e.clientX - rect.left;
            mouse.current.y = e.clientY - rect.top;
            mouse.current.active = true;
        };

        const handleMouseLeave = () => {
            mouse.current.active = false;
        };

        const draw = () => {

            ctx.clearRect(
                0,
                0,
                canvas.width,
                canvas.height
            );

            particles.forEach((particle) => {

                // Normal movement
                particle.x += particle.vx;
                particle.y += particle.vy;

                // Screen boundaries
                if (
                    particle.x < 0 ||
                    particle.x > canvas.width
                ) {
                    particle.vx *= -1;
                }

                if (
                    particle.y < 0 ||
                    particle.y > canvas.height
                ) {
                    particle.vy *= -1;
                }

                // Mouse interaction
                if (
                    mouse.current.active &&
                    mouse.current.x !== null
                ) {

                    const dx =
                        mouse.current.x - particle.x;

                    const dy =
                        mouse.current.y - particle.y;

                    const distance =
                        Math.sqrt(dx * dx + dy * dy);

                    const interactionDistance = 180;

                    if (distance < interactionDistance) {

                        const force =
                            (interactionDistance - distance) /
                            interactionDistance;

                        particle.x += dx * force * 0.015;
                        particle.y += dy * force * 0.015;
                    }
                }

                // Particle
                ctx.beginPath();

                ctx.arc(
                    particle.x,
                    particle.y,
                    particle.size,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle =
                    `rgba(70, 120, 255, ${particle.opacity})`;

                ctx.fill();
            });

            // Connecting network
            for (let i = 0; i < particles.length; i++) {

                for (
                    let j = i + 1;
                    j < particles.length;
                    j++
                ) {

                    const dx =
                        particles[i].x -
                        particles[j].x;

                    const dy =
                        particles[i].y -
                        particles[j].y;

                    const distance =
                        Math.sqrt(dx * dx + dy * dy);

                    if (distance < 110) {

                        const opacity =
                            1 - distance / 110;

                        ctx.beginPath();

                        ctx.moveTo(
                            particles[i].x,
                            particles[i].y
                        );

                        ctx.lineTo(
                            particles[j].x,
                            particles[j].y
                        );

                        ctx.strokeStyle =
                            `rgba(80, 120, 255, ${opacity * 0.25})`;

                        ctx.lineWidth = 0.7;

                        ctx.stroke();
                    }
                }
            }

            // Mouse glow
            if (
                mouse.current.active &&
                mouse.current.x !== null
            ) {

                const gradient =
                    ctx.createRadialGradient(
                        mouse.current.x,
                        mouse.current.y,
                        0,
                        mouse.current.x,
                        mouse.current.y,
                        180
                    );

                gradient.addColorStop(
                    0,
                    "rgba(235, 240, 255, 0.15)"
                );

                gradient.addColorStop(
                    0.5,
                    "rgba(203, 197, 255, 0.06)"
                );

                gradient.addColorStop(
                    1,
                    "rgba(255, 255, 255, 0)"
                );

                ctx.beginPath();

                ctx.arc(
                    mouse.current.x,
                    mouse.current.y,
                    180,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle = gradient;

                ctx.fill();
            }

            // Mouse connection lines
            if (
                mouse.current.active &&
                mouse.current.x !== null
            ) {

                particles.forEach((particle) => {

                    const dx =
                        mouse.current.x - particle.x;

                    const dy =
                        mouse.current.y - particle.y;

                    const distance =
                        Math.sqrt(dx * dx + dy * dy);

                    if (distance < 160) {

                        const opacity =
                            1 - distance / 160;

                        ctx.beginPath();

                        ctx.moveTo(
                            mouse.current.x,
                            mouse.current.y
                        );

                        ctx.lineTo(
                            particle.x,
                            particle.y
                        );

                        ctx.strokeStyle =
                            `rgba(70, 110, 255, ${opacity * 0.5})`;

                        ctx.lineWidth = 1;

                        ctx.stroke();
                    }
                });
            }

            animationFrame =
                requestAnimationFrame(draw);
        };

        window.addEventListener(
            "resize",
            resizeCanvas
        );

        canvas.parentElement.addEventListener(
            "mousemove",
            handleMouseMove
        );

        canvas.parentElement.addEventListener(
            "mouseleave",
            handleMouseLeave
        );

        resizeCanvas();
        draw();

        return () => {

            cancelAnimationFrame(
                animationFrame
            );

            window.removeEventListener(
                "resize",
                resizeCanvas
            );

            canvas.parentElement.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            canvas.parentElement.removeEventListener(
                "mouseleave",
                handleMouseLeave
            );
        };

    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="ai-network-background"
        />
    );
};

export default AIBackground;